import { useEffect, useState } from "react";
import { Modal, ModalBody } from "reactstrap";

import { getMcapImplementationMoment, getMcapImplementationSingleMoment } from "api/api.service";
import { CONTEXT_LABELS, STATE_LABELS, GRADE_LABELS } from "../labels";
import typography from "../typography";

/**
 * The classrooms behind one reset moment.
 *
 * Opens from a row on the moment table, so "Before a test · 3 classrooms"
 * becomes the three classrooms an administrator can actually go and talk to.
 * The row alone was a dead end: a number with no way to act on it.
 *
 * CLASSROOM LEVEL, AND NO FURTHER. Every row is a section. No teacher is named
 * and no student appears anywhere in this payload — a name beside a low number
 * turns a coaching conversation into a performance review, which is the one
 * thing this dashboard has promised throughout not to become.
 *
 * Fetched on open rather than prefetched with the summary: five moments times
 * every classroom is a much larger payload than the dashboard needs, and most
 * of it is never looked at.
 */
// The sentinel the "needs support" card opens with. Same dialog, same card
// list — the only differences are the heading and that each row reports the one
// moment that classroom already uses instead of its most-picked state.
export const SINGLE_MOMENT = "SINGLE_MOMENT";

export default function MomentDetailModal({ context, filters, onClose }) {
  const isSingle = context === SINGLE_MOMENT;
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  useEffect(() => {
    if (!context) return undefined;
    const controller = new AbortController();
    setLoading(true);
    setError(false);
    const request = isSingle
      ? getMcapImplementationSingleMoment(filters, controller.signal)
      : getMcapImplementationMoment({ ...filters, context }, controller.signal);
    request
      .then((res) => setData(res || null))
      .catch((e) => {
        // An aborted request is the component unmounting, not a failure —
        // showing an error card for it would flash a lie on every quick close.
        if (e?.name !== "AbortError" && e?.name !== "CanceledError") setError(true);
      })
      .finally(() => setLoading(false));
    return () => controller.abort();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [context, isSingle, JSON.stringify(filters)]);

  const rows = data?.rows ?? [];
  // Bars are scaled to the busiest classroom in this dialog, not to an absolute
  // maximum: the question here is who is doing this most, relative to the rest.
  const maxResets = rows.reduce((m, r) => Math.max(m, r.resets), 0) || 1;
  const fmtDate = (iso) => (iso ? new Date(iso).toLocaleDateString(undefined, { day: "numeric", month: "short" }) : "—");

  return (
    <Modal isOpen={Boolean(context)} toggle={onClose} centered size="xl" contentClassName="mcap-moment-modal">
      <ModalBody>
        <div className="mcap-moment-modal-head">
          <div>
            <p className="mcap-moment-modal-eyebrow">
              {isSingle ? typography.MOMENT_MODAL_SINGLE_EYEBROW : typography.MOMENT_MODAL_EYEBROW}
            </p>
            <h4 className="mcap-moment-modal-title">
              {isSingle ? typography.MOMENT_MODAL_SINGLE_TITLE : CONTEXT_LABELS[context] || context}
            </h4>
            {!loading && data && (
              <p className="mcap-moment-modal-sub">
                {data.classrooms} classroom{data.classrooms === 1 ? "" : "s"} · {data.resets} reset
                {data.resets === 1 ? "" : "s"} in this period
              </p>
            )}
          </div>
          <button type="button" className="mcap-moment-modal-close" onClick={onClose} aria-label="Close">
            <i className="mdi mdi-close" />
          </button>
        </div>

        {loading && <p className="mcap-moment-modal-state">Loading…</p>}
        {!loading && error && <p className="mcap-moment-modal-state">{typography.MOMENT_MODAL_ERROR}</p>}
        {!loading && !error && rows.length === 0 && (
          <p className="mcap-moment-modal-state">
            {isSingle ? typography.MOMENT_MODAL_SINGLE_EMPTY : typography.MOMENT_MODAL_EMPTY}
          </p>
        )}

        {!loading && !error && rows.length > 0 && (
          <>
            {/* Column labels once, at the top — not stamped onto all 22 rows.
                Repeating "BACK ON TASK / USEFULNESS / LAST USED" per card was
                what made this read as a spreadsheet with extra steps: the same
                three words appeared more times than any actual value. */}
            <div className="mcap-cl-head" aria-hidden="true">
              <span>Classroom</span>
              <span className="mcap-cl-head-vol">Resets</span>
              <span>{isSingle ? typography.MOMENT_ONLY_LABEL : "Most-picked need"}</span>
              {!isSingle && <span className="mcap-cl-head-num">Back on task</span>}
              {!isSingle && <span className="mcap-cl-head-num">Usefulness</span>}
              <span className="mcap-cl-head-num">Last used</span>
            </div>

            <ul className={`mcap-cl-list${isSingle ? " is-single" : ""}`}>
              {rows.map((r) => (
                <li key={r.sectionId} className="mcap-cl">
                  <div className="mcap-cl-id">
                    <p className="mcap-cl-name">{r.classroom}</p>
                    <p className="mcap-cl-meta">
                      {r.school}
                      {r.gradeBand ? ` · ${GRADE_LABELS[r.gradeBand] || r.gradeBand}` : ""}
                    </p>
                  </div>

                  <div className="mcap-cl-volume">
                    <span className="mcap-cl-bar">
                      <span className="mcap-cl-bar-fill" style={{ width: `${(r.resets / maxResets) * 100}%` }} />
                    </span>
                    <span className="mcap-cl-resets">{r.resets}</span>
                  </div>

                  <div className="mcap-cl-tag">
                    {isSingle ? (
                      r.onlyMoment ? (
                        <span className="mcap-cl-chip">{CONTEXT_LABELS[r.onlyMoment] || r.onlyMoment}</span>
                      ) : (
                        <span className="mcap-cl-muted">—</span>
                      )
                    ) : r.topState ? (
                      <span className="mcap-cl-chip">{STATE_LABELS[r.topState] || r.topState}</span>
                    ) : (
                      <span className="mcap-cl-muted">—</span>
                    )}
                  </div>

                  {!isSingle && (
                    <span className="mcap-cl-num">
                      {r.backOnTaskUnderTwo === null ? (
                        <span className="mcap-cl-muted">—</span>
                      ) : (
                        <>
                          {r.backOnTaskUnderTwo}
                          <span className="mcap-cl-num-of"> / {r.outcomesLogged}</span>
                        </>
                      )}
                    </span>
                  )}

                  {!isSingle && (
                    <span className="mcap-cl-num">
                      {r.avgUsefulness === null ? (
                        <span className="mcap-cl-muted">—</span>
                      ) : (
                        <>
                          {r.avgUsefulness}
                          {/* 1-10 scale, per LogClassActivityOutcomeRequest. */}
                          <span className="mcap-cl-num-of"> / 10</span>
                        </>
                      )}
                    </span>
                  )}

                  <span className="mcap-cl-num mcap-cl-date">{fmtDate(r.lastUsedAt)}</span>
                </li>
              ))}
            </ul>

            {/* The dash explanation only belongs on the view that can show a
                dash — the single-moment list reports no outcome columns at all,
                so explaining a symbol that never appears there would just be
                noise. The privacy line applies to both. */}
            <p className="mcap-moment-modal-foot">
              {!isSingle && (
                <>
                  A dash means fewer than {data.aggregationThreshold} logged outcomes — too few to report without
                  describing one classroom&apos;s handful of resets.{" "}
                </>
              )}
              Figures are classroom-level; no teacher or student is identified.
            </p>
          </>
        )}

      </ModalBody>
    </Modal>
  );
}
